// Crescendo: native dialog viewer for the custom product gallery only.
// Native modal behaviour supplies focus containment, Escape and focus restoration.
import { enableMobileGalleryLoop } from '@theme/scrolling';
import { ZoomMediaSelectedEvent } from '@theme/events';

class ProductImageLightbox extends HTMLElement {
  connectedCallback() {
    this.listeners?.abort();
    this.listeners = new AbortController();
    const { signal } = this.listeners;
    const mobileStrip = this.querySelector('.pm__mobile-strip');
    enableMobileGalleryLoop(mobileStrip, {
      signal,
      onLoop: (step) => {
        const slides = mobileStrip.querySelectorAll('.pm__mobile-slide');
        const slide = step > 0 ? slides[0] : slides[slides.length - 1];
        if (!slide) return;
        mobileStrip.scrollBy({
          left: slide.getBoundingClientRect().left - mobileStrip.getBoundingClientRect().left - mobileStrip.clientLeft,
          behavior: 'instant',
        });
      },
    });
    this.dialog = this.querySelector('.pm-lightbox');
    const data = this.querySelector('[data-lightbox-images]');
    if (!this.dialog || !data) return;

    this.images = JSON.parse(data.textContent);
    this.image = this.dialog.querySelector('.pm-lightbox__image');
    this.counter = this.dialog.querySelector('.pm-lightbox__counter');
    this.index = 0;
    let start;
    let moved = false;

    this.addEventListener('pointerdown', (event) => {
      start = { x: event.clientX, y: event.clientY, target: event.target, type: event.pointerType };
      moved = false;
    }, { signal });
    this.addEventListener('pointermove', (event) => {
      if (start && Math.hypot(event.clientX - start.x, event.clientY - start.y) > 12) moved = true;
    }, { signal });
    this.addEventListener('pointercancel', () => { start = null; moved = true; }, { signal });
    this.addEventListener('pointerup', (event) => {
      if (start && this.dialog.open && start.type === 'touch' && start.target.closest('.pm-lightbox__stage')) {
        const dx = event.clientX - start.x;
        const dy = event.clientY - start.y;
        if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) {
          moved = true;
          this.select(this.index + (dx < 0 ? 1 : -1));
        }
      }
      start = null;
    }, { signal });

    this.addEventListener('click', (event) => {
      // A swipe/drag should never accidentally open or dismiss the viewer.
      if (moved && event.detail !== 0) return;
      const opener = event.target.closest('[data-open-image]');
      if (opener) {
        const currentId = this.querySelector('.pm__primary-img')?.dataset.imageId;
        const index = opener.dataset.openImage === 'current'
          ? this.images.findIndex((img) => String(img.id) === currentId)
          : Number(opener.dataset.openImage);
        this.select(Math.max(0, index));
        this.opener = opener;
        this.dialog.showModal();
        return;
      }
      if (!this.dialog.open) return;
      const step = event.target.closest('[data-lightbox-step]');
      if (step) this.select(this.index + Number(step.dataset.lightboxStep));
      else if (event.target.closest('[data-lightbox-close]') || event.target === this.dialog || event.target.classList.contains('pm-lightbox__stage')) {
        this.dialog.close();
      }
    }, { signal });

    this.dialog.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault();
        this.select(this.index + (event.key === 'ArrowRight' ? 1 : -1));
      }
    }, { signal });
    this.dialog.addEventListener('close', () => {
      this.opener?.focus({ preventScroll: true });
    }, { signal });
  }

  /**
   * Opens an image by its position in the source media gallery (which may include videos).
   * @param {number} galleryIndex
   * @param {HTMLElement} opener
   */
  openGalleryImage(galleryIndex, opener) {
    const index = this.images?.findIndex((image) => image.galleryIndex === galleryIndex) ?? -1;
    if (index < 0) return;
    this.opener = opener;
    this.select(index);
    this.dialog.showModal();
  }

  /** @param {HTMLElement | undefined} opener - Visible gallery control to focus after closing. */
  setReturnFocus(opener) {
    if (opener) this.opener = opener;
  }

  select(index) {
    this.index = (index + this.images.length) % this.images.length;
    const image = this.images[this.index];
    this.image.alt = image.alt;
    this.image.width = image.width;
    this.image.height = image.height;
    this.image.sizes = '(max-width: 749px) calc(100vw - 24px), calc(100vw - 128px)';
    this.image.srcset = image.srcset;
    this.image.src = image.src;
    this.image.hidden = false;
    this.counter.textContent = `${this.index + 1} / ${this.images.length}`;
    if (Number.isInteger(image.galleryIndex)) {
      this.dispatchEvent(new ZoomMediaSelectedEvent(image.galleryIndex));
    }
  }

  disconnectedCallback() {
    this.listeners?.abort();
    if (this.dialog?.open) this.dialog.close();
  }
}

if (!customElements.get('product-image-lightbox')) {
  customElements.define('product-image-lightbox', ProductImageLightbox);
}
